import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { useApp } from '../context/AppContext';
import { formatPct, formatCurrency, growthPct } from '../lib/format';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Download, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import HierarchicalAssetTable from '../components/HierarchicalAssetTable';
import SavedViewsBar from '../components/SavedViewsBar';
import { convertedCost, convertedMarketValue, convertedRows, fxForSource } from '../lib/currency';

import { MoneyModeCtx } from '../components/dashboards/MoneyMode';
import PeriodPicker, { MAX_COMPARE } from '../components/dashboards/PeriodPicker';
import PeriodSummaryTable from '../components/dashboards/PeriodSummaryTable';
import SourceSummaryTable from '../components/dashboards/SourceSummaryTable';
import GroupedComparisonTable from '../components/dashboards/GroupedComparisonTable';

const fmtMoney = (n, mode) => formatCurrency(n, 'INR', { mode });

const GrowthCell = ({ curr, prev }) => {
  const g = growthPct(curr, prev);
  if (g === null || g === 0) {
    return <span className="chip chip-neutral"><Minus size={10} />0.00%</span>;
  }
  const positive = g > 0;
  return (
    <span className={`chip ${positive ? 'chip-positive' : 'chip-negative'}`}>
      {positive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
      {formatPct(g)}
    </span>
  );
};


export default function Dashboards() {
  const { snapshots, holdings, sources, currency, fxRates, dashboardViews = [], addDashboardView, updateDashboardView, deleteDashboardView } = useApp();
  // Default: pick the most recent MAX_COMPARE reporting dates
  const [selected, setSelected] = useState(() => {
    const sorted = [...snapshots].sort((a, b) => a.date.localeCompare(b.date));
    return sorted.slice(-MAX_COMPARE).map((s) => s.id);
  });
  const selectedSnapshots = snapshots.filter((s) => selected.includes(s.id));

  // Build per-snapshot FX lookup for conversion
  // Pass the full fxRates state through — helpers (fxForSource / convertedRows) pick the
  // right per-source or default rate internally.
  const fxMap = fxRates || {};

  // ---------- Filter options (derived from all visible holdings across selected snapshots) ----------
  const { subOptions, geoOptions, stratOptions } = useMemo(() => {
    const sub = new Set(), geo = new Set(), strat = new Set();
    selectedSnapshots.forEach((s) => {
      Object.values(holdings[s.id] || {}).flat().forEach((r) => {
        if (r.subAssetClass) sub.add(r.subAssetClass);
        if (r.geography) geo.add(r.geography);
        if (r.marketCap) strat.add(r.marketCap);
      });
    });
    const toOpt = (set) => Array.from(set).sort().map((v) => ({ value: v, label: v }));
    return { subOptions: toOpt(sub), geoOptions: toOpt(geo), stratOptions: toOpt(strat) };
  }, [selectedSnapshots, holdings]);

  const [subSel, setSubSel] = useState([]);
  const [geoSel, setGeoSel] = useState([]);
  const [stratSel, setStratSel] = useState([]);
  const [showPct, setShowPct] = useState(false);
  // Money-format mode persisted across reloads; default is full Indian-comma figures
  const [moneyMode, setMoneyMode] = useState(() => {
    try { return localStorage.getItem('dash-money-mode') || 'full'; } catch (_) { return 'full'; }
  });
  useEffect(() => {
    try { localStorage.setItem('dash-money-mode', moneyMode); } catch (_) {}
  }, [moneyMode]);

  const anyFilter = subSel.length > 0 || geoSel.length > 0 || stratSel.length > 0;
  const filterRow = useMemo(() => {
    const subSet = new Set(subSel), geoSet = new Set(geoSel), stratSet = new Set(stratSel);
    return (r) => {
      if (subSet.size && !subSet.has(r.subAssetClass)) return false;
      if (geoSet.size && !geoSet.has(r.geography)) return false;
      if (stratSet.size && !stratSet.has(r.marketCap)) return false;
      return true;
    };
  }, [subSel, geoSel, stratSel]);

  // Pre-filter holdings so every downstream component sees the same filtered data
  const filteredHoldings = useMemo(() => {
    if (!anyFilter) return holdings;
    const out = {};
    Object.entries(holdings).forEach(([snapId, bySource]) => {
      const nextBySource = {};
      Object.entries(bySource || {}).forEach(([srcId, rows]) => {
        nextBySource[srcId] = (rows || []).filter(filterRow);
      });
      out[snapId] = nextBySource;
    });
    return out;
  }, [holdings, filterRow, anyFilter]);

  const clearFilters = () => { setSubSel([]); setGeoSel([]); setStratSel([]); };

  // ---------- Saved Views ----------
  const applyView = (v) => {
    // Only keep reporting dates that still exist
    const validSel = (v.selected || []).filter((id) => snapshots.some((s) => s.id === id));
    setSelected(validSel);
    setSubSel(v.subSel || []);
    setGeoSel(v.geoSel || []);
    setStratSel(v.stratSel || []);
    setShowPct(!!v.showPct);
  };
  const saveCurrentView = (name, description) => {
    addDashboardView({
      name,
      description: description || '',
      selected,
      subSel,
      geoSel,
      stratSel,
      showPct,
    });
  };
  const updateView = (id, patch) => updateDashboardView(id, patch);
  const removeView = (id) => deleteDashboardView(id);

  // ---------- URL ?view=ID handling (clicking from Sidebar) ----------
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get('view');
    if (!id) return;
    const v = dashboardViews.find((x) => x.id === id);
    if (v) {
      applyView(v);
    }
    // remove the param so subsequent edits are not blocked
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, dashboardViews]);

  const toggle = (id) =>
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_COMPARE) return prev; // cap reached; ignore
      return [...prev, id].sort((a, b) => {
        const da = snapshots.find((s) => s.id === a)?.date || '';
        const db = snapshots.find((s) => s.id === b)?.date || '';
        return da.localeCompare(db);
      });
    });
  const clearAll = () => setSelected([]);

  // Headline KPIs based on latest selected snapshot and first (INR-converted) — respects filters
  const latest = selectedSnapshots[selectedSnapshots.length - 1];
  const earliest = selectedSnapshots[0];
  const aggFor = (snap) => {
    if (!snap) return { cost: 0, marketValue: 0, pnl: 0 };
    const enriched = convertedRows(filteredHoldings[snap.id], fxMap, snap.id);
    const cost = enriched.reduce((a, x) => a + x.cost, 0);
    const mv = enriched.reduce((a, x) => a + x.mv, 0);
    return { cost, marketValue: mv, pnl: mv - cost };
  };
  const current = aggFor(latest);
  const base = aggFor(earliest);

  // ---------- Export view → multi-sheet workbook ----------
  const buildExportPayload = () => {
    // Per-snap enriched arrays (used by every sheet below)
    const perSnap = selectedSnapshots.map((snap) => ({
      snap,
      enriched: convertedRows(filteredHoldings[snap.id], fxMap, snap.id),
    }));

    // ---------- Sheet 1: Period summary ----------
    const periodRows = perSnap.map(({ snap, enriched }) => {
      const cost = enriched.reduce((a, x) => a + x.cost, 0);
      const mv = enriched.reduce((a, x) => a + x.mv, 0);
      const pnl = mv - cost;
      const pnlPct = cost > 0 ? pnl / cost : 0;
      return [
        snap.label,
        snap.date,
        snap.shared_from ? `${snap.shared_from.email} (${snap.shared_role})` : '—',
        cost, mv, pnl, pnlPct,
      ];
    });
    const periodTotals = (() => {
      const c = periodRows.reduce((a, r) => a + r[3], 0);
      const m = periodRows.reduce((a, r) => a + r[4], 0);
      return ['Total', '', '', c, m, m - c, c > 0 ? (m - c) / c : 0];
    })();

    // Generic builder that pivots a group (e.g. asset class) wide across snapshots
    const buildPivot = (label, keyFn, sortFn) => {
      const groupSet = new Set();
      perSnap.forEach(({ enriched }) => enriched.forEach(({ row }) => {
        const k = keyFn(row);
        if (k) groupSet.add(k);
      }));
      const groups = Array.from(groupSet).sort(sortFn || ((a, b) => a.localeCompare(b)));

      const headers = [label];
      perSnap.forEach(({ snap }) => {
        headers.push(`Cost · ${snap.label}`);
        headers.push(`MV · ${snap.label}`);
      });
      headers.push('Total cost'); headers.push('Total MV');

      const numericCols = [];
      for (let i = 2; i <= headers.length; i++) numericCols.push(i);

      const rows = groups.map((g) => {
        const out = [g];
        let totalCost = 0, totalMv = 0;
        perSnap.forEach(({ enriched }) => {
          let cost = 0, mv = 0;
          enriched.forEach((x) => { if (keyFn(x.row) === g) { cost += x.cost; mv += x.mv; } });
          out.push(cost); out.push(mv);
          totalCost += cost; totalMv += mv;
        });
        out.push(totalCost); out.push(totalMv);
        return out;
      });

      const totals = ['Total'];
      let grand = { c: 0, m: 0 };
      perSnap.forEach(({ enriched }) => {
        let cost = 0, mv = 0;
        enriched.forEach((x) => { cost += x.cost; mv += x.mv; });
        totals.push(cost); totals.push(mv);
        grand.c += cost; grand.m += mv;
      });
      totals.push(grand.c); totals.push(grand.m);

      const widths = [28, ...Array(headers.length - 1).fill(18)];
      return { headers, rows, numeric_cols: numericCols, totals, totals_label_span: 1, widths };
    };

    const assetClassSheet = buildPivot('Main Asset Class', (r) => r.mainAssetClass || '(unspecified)');
    const geographySheet  = buildPivot('Geography',        (r) => r.geography || '(unspecified)');
    const strategySheet   = buildPivot('Strategy',         (r) => r.marketCap || '(unspecified)');

    // Source Summary sheet — one row per source, Rows / Cost / MV / P&L per snapshot
    const sourceSummarySheet = (() => {
      const headers = ['Source'];
      perSnap.forEach(({ snap }) => {
        headers.push(`Rows · ${snap.label}`);
        headers.push(`Cost · ${snap.label}`);
        headers.push(`MV · ${snap.label}`);
        headers.push(`P&L · ${snap.label}`);
        headers.push(`P&L % · ${snap.label}`);
      });

      // Numeric (currency) columns: skip the row-count columns
      const numericCols = [];
      const pctCols = [];
      for (let i = 0; i < perSnap.length; i++) {
        const base = 1 + i * 5; // column index of "Rows ..."
        numericCols.push(base + 2); // Cost
        numericCols.push(base + 3); // MV
        numericCols.push(base + 4); // P&L
        pctCols.push(base + 5);     // P&L %
      }

      const rows = sources.map((src) => {
        const out = [src.name];
        perSnap.forEach(({ snap }) => {
          const srcRows = (filteredHoldings[snap.id] || {})[src.id] || [];
          const fx = fxForSource(fxMap, snap.id, src.id);
          let cost = 0, mv = 0;
          srcRows.forEach((r) => { cost += convertedCost(r, fx); mv += convertedMarketValue(r, fx); });
          const pnl = mv - cost;
          const pnlPct = cost > 0 ? pnl / cost : 0;
          out.push(srcRows.length, cost, mv, pnl, pnlPct);
        });
        return out;
      });

      const totals = ['Total'];
      perSnap.forEach(({ snap }) => {
        let r = 0, c = 0, m = 0;
        sources.forEach((src) => {
          const srcRows = (filteredHoldings[snap.id] || {})[src.id] || [];
          const fx = fxForSource(fxMap, snap.id, src.id);
          r += srcRows.length;
          srcRows.forEach((row) => { c += convertedCost(row, fx); m += convertedMarketValue(row, fx); });
        });
        totals.push(r, c, m, m - c, c > 0 ? (m - c) / c : 0);
      });

      const widths = [28, ...Array(headers.length - 1).fill(16)];
      return { headers, rows, numeric_cols: numericCols, pct_cols: pctCols, totals, totals_label_span: 1, widths };
    })();

    // Source/Institution sheet — use source name + institution from rows
    const srcLookup = Object.fromEntries(sources.map((s) => [s.id, s.name]));
    const sourcePivot = (() => {
      // Group key = "{source name} · {institution|'—'}"
      const headers = ['Source · Institution'];
      perSnap.forEach(({ snap }) => {
        headers.push(`Cost · ${snap.label}`);
        headers.push(`MV · ${snap.label}`);
      });
      headers.push('Total cost'); headers.push('Total MV');

      const grouped = new Map();
      perSnap.forEach(({ snap, enriched }) => {
        enriched.forEach((x) => {
          const srcId = x.srcId;
          const inst = (x.row.institution || '—');
          const key = `${srcLookup[srcId] || srcId} · ${inst}`;
          if (!grouped.has(key)) grouped.set(key, perSnap.map(() => ({ cost: 0, mv: 0 })));
          const arr = grouped.get(key);
          const idx = perSnap.findIndex((p) => p.snap.id === snap.id);
          arr[idx].cost += x.cost;
          arr[idx].mv += x.mv;
        });
      });

      const rows = Array.from(grouped.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, arr]) => {
          const out = [key];
          let tc = 0, tm = 0;
          arr.forEach((v) => { out.push(v.cost); out.push(v.mv); tc += v.cost; tm += v.mv; });
          out.push(tc); out.push(tm);
          return out;
        });

      const numericCols = [];
      for (let i = 2; i <= headers.length; i++) numericCols.push(i);

      const totals = ['Total'];
      let gc = 0, gm = 0;
      perSnap.forEach(({ enriched }) => {
        let cost = 0, mv = 0;
        enriched.forEach((x) => { cost += x.cost; mv += x.mv; });
        totals.push(cost); totals.push(mv);
        gc += cost; gm += mv;
      });
      totals.push(gc); totals.push(gm);

      const widths = [42, ...Array(headers.length - 1).fill(18)];
      return { headers, rows, numeric_cols: numericCols, totals, totals_label_span: 1, widths };
    })();

    return {
      title: 'Track · Dashboard Export',
      metadata: [
        ['Generated', new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })],
        ['Reporting dates', selectedSnapshots.map((s) => s.label).join(', ') || '(none)'],
        ['Sub-asset filter', subSel.join(', ') || '(all)'],
        ['Geography filter', geoSel.join(', ') || '(all)'],
        ['Strategy filter', stratSel.join(', ') || '(all)'],
        ['Currency mode', moneyMode],
      ],
      sheets: [
        {
          name: 'Period Summary',
          title: 'Period summary',
          subtitle: 'One row per reporting date — cost, market value, P&L and P&L %',
          headers: ['Reporting date', 'ISO date', 'Shared from', 'Cost (₹)', 'Market value (₹)', 'P&L (₹)', 'P&L %'],
          rows: periodRows,
          numeric_cols: [4, 5, 6],
          pct_cols: [7],
          totals: periodTotals,
          widths: [18, 14, 28, 18, 18, 18, 14],
        },
        { name: 'Asset Class', title: 'By main asset class', ...assetClassSheet },
        { name: 'Geography',   title: 'By geography',          ...geographySheet },
        { name: 'Strategy',    title: 'By strategy',           ...strategySheet },
        { name: 'Source Summary', title: 'By source',           ...sourceSummarySheet },
        { name: 'Source × Inst', title: 'By source · institution', ...sourcePivot },
      ],
    };
  };

  const handleExport = async () => {
    if (!selectedSnapshots.length) {
      toast.error('Pick at least one reporting date first');
      return;
    }
    try {
      const payload = buildExportPayload();
      const blob = await api.excelDashboard(payload);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `track-dashboard_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Dashboard exported');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  return (
    <MoneyModeCtx.Provider value={moneyMode}>
      <PageHeader
        overline="01 · Analyse"
        title="Portfolio Dashboards"
        description="Four tabular views of your portfolio. Compare up to 4 reporting dates side-by-side — dates available are synced with Data Entry."
        actions={
          <button className="btn-secondary" onClick={handleExport} data-testid="export-dashboard-button">
            <Download size={14} /> Export view
          </button>
        }
      />

      {/* Saved Views */}
      <SavedViewsBar
        views={dashboardViews}
        currentState={{ selected, subSel, geoSel, stratSel, showPct }}
        onApply={applyView}
        onSave={saveCurrentView}
        onUpdate={updateView}
        onDelete={removeView}
      />

      {/* KPI strip */}
      {latest && (
        <div className="grid grid-cols-4 gap-px bg-border border border-border mb-8" data-testid="kpi-strip">
          <div className="bg-white p-6">
            <div className="overline mb-2">Net worth · {latest.label}</div>
            <div className="font-heading text-[28px] font-bold tracking-tight tabular-nums">
              {fmtMoney(current.marketValue, moneyMode)}
            </div>
            {earliest && earliest !== latest && (
              <div className="mt-1 text-xs text-[#575757]">
                vs {earliest.label}: <GrowthCell curr={current.marketValue} prev={base.marketValue} />
              </div>
            )}
          </div>
          <div className="bg-white p-6">
            <div className="overline mb-2">Total cost</div>
            <div className="font-heading text-[28px] font-bold tracking-tight tabular-nums">
              {fmtMoney(current.cost, moneyMode)}
            </div>
            <div className="mt-1 text-xs text-[#575757]">
              Invested capital
            </div>
          </div>
          <div className="bg-white p-6">
            <div className="overline mb-2">Unrealised P&amp;L</div>
            <div className={`font-heading text-[28px] font-bold tracking-tight tabular-nums ${current.pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
              {current.pnl >= 0 ? '+' : ''}{fmtMoney(current.pnl, moneyMode)}
            </div>
            <div className="mt-1 text-xs text-[#575757]">
              {current.cost > 0 ? formatPct((current.pnl / current.cost) * 100) : '—'} return on cost
            </div>
          </div>
          <div className="bg-white p-6">
            <div className="overline mb-2">Coverage</div>
            <div className="font-heading text-[28px] font-bold tracking-tight tabular-nums">
              {sources.length}<span className="text-base text-[#787872] font-normal"> sources</span>
            </div>
            <div className="mt-1 text-xs text-[#575757]">
              {snapshots.length} snapshots captured
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <PeriodPicker snapshots={snapshots} selected={selected} onToggle={toggle} onClear={clearAll} />
      </div>

      <Tabs defaultValue="summary" data-testid="dashboard-tabs">
        <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start p-0 gap-0 h-auto">
          {[
            ['summary',    '1. Period Summary'],
            ['sourceSummary', '2. Source Summary'],
            ['assetClass', '3. Asset Class'],
            ['geoCap',     '4. Geography & Strategy'],
            ['mainStrat',  '5. Asset Class × Strategy'],
          ].map(([v, label]) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`tab-${v}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[hsl(var(--primary))] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[hsl(var(--primary))] text-[13px] font-heading font-semibold text-[#575757] px-4 py-3"
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Filter bar: between dashboard tab name and table content; applies to all tabs */}
        <div className="border border-border bg-[#FAFAF7] p-4 mt-4 flex flex-wrap items-end gap-4" data-testid="dashboard-filters">
          <div className="overline mr-2 self-center text-[#575757]">Filters apply to all tabs</div>
          <MultiSelectDropdown
            label="Sub-Asset Class"
            options={subOptions}
            selected={subSel}
            onChange={setSubSel}
            placeholder="All sub-classes"
            testId="filter-sub-asset"
            width={200}
          />
          <MultiSelectDropdown
            label="Geography"
            options={geoOptions}
            selected={geoSel}
            onChange={setGeoSel}
            placeholder="All geographies"
            testId="filter-geography"
            width={180}
          />
          <MultiSelectDropdown
            label="Strategy"
            options={stratOptions}
            selected={stratSel}
            onChange={setStratSel}
            placeholder="All strategies"
            testId="filter-strategy"
            width={180}
          />
          <label className="flex items-center gap-2 cursor-pointer select-none ml-2" data-testid="toggle-pct-of-total">
            <Checkbox
              checked={showPct}
              onCheckedChange={(v) => setShowPct(!!v)}
              className="border-[#B0B0A8] data-[state=checked]:bg-[hsl(var(--primary))] data-[state=checked]:border-[hsl(var(--primary))]"
            />
            <span className="text-[12px] font-heading font-semibold">Show % of column total</span>
          </label>

          {/* Money format toggle */}
          <div className="flex items-center gap-1 ml-2" data-testid="money-mode-toggle">
            <span className="overline mr-1">Format</span>
            {[
              { v: 'full',    label: 'Full ₹' },
              { v: 'lacs',    label: 'In Lacs' },
              { v: 'compact', label: 'Cr / L' },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setMoneyMode(opt.v)}
                data-testid={`money-mode-${opt.v}`}
                className={`text-[11px] font-heading font-semibold px-2.5 py-1 border transition-colors ${
                  moneyMode === opt.v
                    ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white'
                    : 'bg-white border-border text-[#575757] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {anyFilter && (
            <button
              className="btn-secondary text-[11px] ml-auto"
              onClick={clearFilters}
              data-testid="clear-filters"
            >
              Clear filters
            </button>
          )}
        </div>

        <TabsContent value="summary" className="mt-6">
          <div className="border border-border bg-white overflow-x-auto">
            <PeriodSummaryTable snapshots={selectedSnapshots} holdings={filteredHoldings} currency={currency} fxRates={fxMap} />
          </div>
        </TabsContent>
        <TabsContent value="sourceSummary" className="mt-6">
          <SourceSummaryTable
            snapshots={selectedSnapshots}
            holdings={filteredHoldings}
            sources={sources}
            currency={currency}
            fxRates={fxMap}
          />
        </TabsContent>
        <TabsContent value="assetClass" className="mt-6 space-y-8">
          <HierarchicalAssetTable
            snapshots={selectedSnapshots}
            holdings={filteredHoldings}
            currency={currency}
            fxRates={fxMap}
            showPct={showPct}
            moneyMode={moneyMode}
            parentFn={(r) => r.mainAssetClass || '—'}
            childFn={(r) => r.subAssetClass || '—'}
            parentLabel="Asset Class"
            searchPlaceholder="Search main or sub-asset class…"
            testIdPrefix="asset-class-hierarchy"
          />
        </TabsContent>
        <TabsContent value="geoCap" className="mt-6 space-y-8">
          <HierarchicalAssetTable
            snapshots={selectedSnapshots}
            holdings={filteredHoldings}
            currency={currency}
            fxRates={fxMap}
            showPct={showPct}
            moneyMode={moneyMode}
            parentFn={(r) => r.geography || '—'}
            childFn={(r) => r.mainAssetClass || '—'}
            parentLabel="Geography → Main Asset Class"
            searchPlaceholder="Search geography or main asset class…"
            testIdPrefix="geography-hierarchy"
          />
          <GroupedComparisonTable label="Strategy" snapshots={selectedSnapshots} holdings={filteredHoldings} currency={currency} groupFn={(r) => r.marketCap || '—'} testId="marketcap-table" fxRates={fxMap} showPct={showPct} />
        </TabsContent>
        <TabsContent value="mainStrat" className="mt-6 space-y-8">
          <HierarchicalAssetTable
            snapshots={selectedSnapshots}
            holdings={filteredHoldings}
            currency={currency}
            fxRates={fxMap}
            showPct={showPct}
            moneyMode={moneyMode}
            parentFn={(r) => r.mainAssetClass || '—'}
            childFn={(r) => r.marketCap || '—'}
            parentLabel="Asset Class × Strategy"
            searchPlaceholder="Search main asset class or strategy…"
            testIdPrefix="main-strategy-hierarchy"
          />
        </TabsContent>
      </Tabs>
    </MoneyModeCtx.Provider>
  );
}
